import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { mergeRefs } from "@/helpers/mergeRefs";
import { twResolve } from "@/helpers/tw-resolve";

import type { DesignSystemElementProps } from "./contracts/dom";
import { fieldControlStateVariants } from "./field-control";
import { TextAreaHighlight } from "./TextAreaHighlight";

const TEXT_AREA_HEIGHT_CLASSES = {
  auto: undefined,
  composer: "min-h-textarea-composer",
  condensed: "min-h-textarea-condensed",
  compact: "min-h-textarea-compact",
  sm: "min-h-textarea-sm",
  md: "min-h-textarea-md",
  lg: "min-h-textarea-lg",
  post: "min-h-textarea-post",
  responsiveCompact: "min-h-textarea-compact @sm:min-h-textarea-condensed",
} as const;

const TEXT_AREA_APPEARANCE_CLASSES = {
  field: ["rounded-lg border border-border-secondary p-space-sm placeholder:text-text-tertiary"],
  bare: ["bg-transparent py-space-sm pl-space-xs placeholder:text-text-tertiary focus:outline-hidden"],
  composer: ["bg-transparent p-0 placeholder:text-text-tertiary focus:outline-hidden"],
} as const;

export type TextAreaHeight = keyof typeof TEXT_AREA_HEIGHT_CLASSES;
export type TextAreaAppearance = keyof typeof TEXT_AREA_APPEARANCE_CLASSES;

export interface TextAreaProps extends DesignSystemElementProps<"textarea"> {
  ref?: React.Ref<HTMLTextAreaElement>;
  appearance?: TextAreaAppearance;
  height?: TextAreaHeight;
  startAdornment?: React.ReactNode;
  toolbar?: React.ReactNode;
  invalid?: boolean;
  hideScrollbar?: boolean;
  pending?: boolean;
  /** UTF-16 offsets within the current value, ordered by start and without overlaps. */
  highlightedRanges?: readonly { start: number; end: number }[];
}

/**
 * A controlled or uncontrolled textarea that grows with its content up to the system maximum.
 * A string value with valid `highlightedRanges` renders the visual overlay described by the prop's range contract.
 */
export function TextArea({
  appearance = "field",
  height = "auto",
  startAdornment,
  toolbar,
  invalid,
  hideScrollbar,
  pending,
  disabled,
  readOnly,
  onChange,
  value,
  ref,
  highlightedRanges = [],
  onScroll,
  ...props
}: TextAreaProps): React.ReactNode {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const highlightedContent = typeof value === "string" && highlightedRanges.length > 0 ? value : undefined;

  const resize = useCallback(() => {
    const element = innerRef.current;
    if (element) {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight + (element.offsetHeight - element.clientHeight) + 1}px`;
    }
  }, []);

  useLayoutEffect(() => resize(), [resize, value, props.placeholder]);
  useEffect(() => {
    const element = innerRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(resize);
    observer.observe(element);

    return () => observer.unobserve(element);
  }, [resize]);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    if (value === undefined) {
      resize();
    }

    onChange?.(event);
  }

  return (
    <div className={twJoin("relative flex w-full flex-col", highlightedContent != null && "isolate")}>
      {toolbar}
      {startAdornment != null && (
        <div className="absolute top-0 left-0 flex h-control-md w-field-adornment items-center justify-center text-text-secondary">
          {startAdornment}
        </div>
      )}
      <textarea
        {...props}
        value={value}
        ref={mergeRefs(innerRef, ref)}
        rows={1}
        onChange={handleChange}
        onScroll={(event) => {
          if (highlightRef.current) {
            highlightRef.current.scrollTop = event.currentTarget.scrollTop;
            highlightRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
          onScroll?.(event);
        }}
        className={twResolve(
          "max-h-textarea-autosize w-full resize-none appearance-none text-body",
          TEXT_AREA_APPEARANCE_CLASSES[appearance],
          TEXT_AREA_HEIGHT_CLASSES[height],
          appearance === "field" && fieldControlStateVariants({ invalid }),
          appearance === "field" &&
            (disabled || readOnly) &&
            "border-border-secondary bg-background-secondary text-text-secondary hover:border-border-secondary",
          startAdornment && "pl-field-adornment",
          toolbar && "rounded-t-none",
          hideScrollbar && "no-scrollbar",
          pending && "pointer-events-none opacity-30",
          highlightedContent != null && "relative z-10 bg-transparent text-transparent caret-text-primary",
          highlightedContent != null && !hideScrollbar && "scrollbar-gutter-stable",
        )}
        aria-invalid={invalid}
        {...{ disabled, readOnly }}
        {...(props.autoComplete === undefined || props.autoComplete === "off" ? autoCompleteDisabledProperties : {})}
      />
      {highlightedContent != null && (
        <TextAreaHighlight
          content={highlightedContent}
          ranges={highlightedRanges}
          appearance={appearance}
          startAdornment={Boolean(startAdornment)}
          wrap={props.wrap}
          reserveScrollbarGutter={!hideScrollbar}
          ref={(element) => {
            highlightRef.current = element;
            if (element) {
              const textArea = innerRef.current;
              element.scrollTop = textArea ? textArea.scrollTop : 0;
              element.scrollLeft = textArea ? textArea.scrollLeft : 0;
            }
          }}
        />
      )}
    </div>
  );
}
